// components/entities/FloorRenderer.tsx
import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Layers } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import type { BaseEntityProps, EntityRowAction, LeasingFloorDTO } from '#types/types';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';
import { useTableGlass, TABLE_GLASS_ROW } from '../portal/tableSurface';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';

export interface FloorRendererProps extends BaseEntityProps<LeasingFloorDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingFloorDTO>[];
}

const DEFAULT_FLOOR_COLUMNS: TableColumnDef[] = [
  { id: 'floor', header: '', minWidth: 180 },
  { id: 'property', header: '', minWidth: 140 },
  { id: 'units', header: '', minWidth: 60, align: 'right' },
  { id: 'plan', header: '', minWidth: 65, align: 'center' },
  { id: 'calibration', header: '', minWidth: 65, align: 'center' },
  { id: 'actions', header: '', minWidth: 60, align: 'right' },
];

export const FloorRenderer = React.memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = [],
}: FloorRendererProps) => {
  const glass = useTableGlass();
  // Subscribe only to the row-level floor and linked property record.
  const floor = useKernelStore((state) => state.inventory.Floor.registry[entityId]);
  const propertyId = floor?.propertyId;
  const propertyRecord = useKernelStore((state) =>
    propertyId ? state.inventory.Property.registry[propertyId] : null
  );

  const propertyName = useMemo(() => propertyRecord?.name, [propertyRecord]);
  const associatedUnitCount =
    floor?.associatedUnitCount ?? (Array.isArray(floor?.unitIds) ? floor.unitIds.length : floor?.unitCount ?? 0);

  const handleSelect = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) onClick();
  }, [onClick]);

  if (!floor) return <EntitySkeleton variant={variant} className={className} />;

  if (variant === 'row') {
    const cellRenderers: Record<string, React.ReactNode> = {
      floor: (
        <div className="min-w-0 h-full flex flex-col justify-center">
          <div className="truncate text-[13px] text-white">{floor.name}</div>
          <div className="truncate text-[10px] text-white/40">Lvl {floor.level}</div>
        </div>
      ),
      property: (
        <div className="min-w-0 text-[12px] text-white/75 truncate h-full flex items-center">
          {propertyName || '...'}
        </div>
      ),
      units: (
        <div className="text-right text-[12px] font-mono text-white/75 h-full flex items-center justify-end w-full">
          {associatedUnitCount}
        </div>
      ),
      plan: (
        <div className="text-[11px] text-center h-full flex items-center justify-center w-full">
          {floor.planFileId ? <span className="text-[#4DAB9A]">YES</span> : <span className="text-white/20">NO</span>}
        </div>
      ),
      calibration: (
        <div className="text-[11px] text-center h-full flex items-center justify-center w-full">
          {floor.calibrationFactor ? <span className="text-[#4DAB9A]">YES</span> : <span className="text-white/20">NO</span>}
        </div>
      ),
      actions: (
        <div className="flex items-center justify-end gap-2 h-full">
          <EntityRowActions entityType="Floor" entity={floor} rowActions={rowActions} />
        </div>
      ),
    };

    const activeColumns = columns ?? DEFAULT_FLOOR_COLUMNS;

    return (
      <div
        className={clsx(
          styles.gridRow,
          styles.bodyRow,
          'group h-11 w-full cursor-pointer border-b border-[#2A2A2A] bg-[#181818] hover:bg-[#1E1E1E]',
          glass && TABLE_GLASS_ROW,
          className
        )}
        onClick={handleSelect}
      >
        {activeColumns.map((column) => (
          <div
            key={column.id}
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={{
                justifyContent:
                column.id === 'actions' || column.align === 'right'
                  ? 'flex-end'
                  : column.align === 'center'
                    ? 'center'
                    : 'flex-start',
            }}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSelect}
      className={clsx(
        'inline-flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/80 hover:bg-white/10',
        className
      )}
    >
      <Layers size={12} />
      <span className="truncate">{floor.name}</span>
    </button>
  );
});
